import { cva, VariantProps } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { isDevelopment } from '~/utils/misc'

import StandardText, {
  StandardTextProps
} from '~/components/Text/StandardText/StandardText'
import Image from '~/components/Helpers/Image'
import BlockVideo from '~/components/Media/components/BlockVideo'

interface TextBlockProps {
  content: {
    contentBlock: StandardTextProps['content']
    reverse: boolean
    image: Image
    video?: Asset
    imageSize: string
    backgroundColour: VariantProps<typeof textBlockVariants>['backgroundColour']
    technicalTexture: Image
  }
}

const textBlockVariants = cva('', {
  variants: {
    backgroundColour: {
      'ink-blue': 'before:bg-blue-d text-white',
      'mid-blue': 'before:bg-blue-m text-white',
      'light-blue': 'before:bg-blue-l text-blue-d',
      chartreuse: 'before:bg-char text-blue-d',
      'light-pink': 'before:bg-pink text-blue-d',
      'light-aqua': 'before:bg-green text-blue-d',
      'light-purple': 'before:bg-purple text-blue-d',
      'light-orange': 'before:bg-orange text-blue-d',
      'light-grey': 'before:bg-grey-l text-blue-d',
      white: 'before:bg-white text-blue-d'
    }
  },
  defaultVariants: {
    backgroundColour: 'ink-blue'
  }
})

const ImageTextOverlay = ({ content }: TextBlockProps) => {
  const {
    contentBlock,
    reverse,
    image,
    video,
    imageSize,
    backgroundColour,
    technicalTexture
  } = content

  const previewUrl =
    isDevelopment && !technicalTexture?.asset.sys.uri.startsWith('https')
      ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
      : ''

  return (
    <div className='@container/imageblock text-left'>
      <div
        className={cn(
          reverse
            ? 'md:before:left-xl flex-col md:flex-row md:before:right-0'
            : 'md:before:right-xl flex-col-reverse md:flex-row-reverse md:before:left-0',
          textBlockVariants({ backgroundColour }),
          'before:absolute before:inset-0 before:-z-10',
          '@min-[57.5rem]/imageblock:md:p-xl @min-[57.5rem]/imageblock:gap-xl @min-[57.5rem]/imageblock:md:gap-2xl relative z-[1] flex items-center p-0',
          reverse
            ? '@max-[57.5rem]/imageblock:flex-col @max-[57.5rem]/imageblock:p-0 @max-[57.5rem]/imageblock:before:left-0'
            : '@max-[57.5rem]/imageblock:flex-col-reverse @max-[57.5rem]/imageblock:p-0 @max-[57.5rem]/imageblock:before:right-0'
        )}
      >
        <div
          className={cn(
            'max-md:aspect-image', // NOTE: temporary fix for collapsed images
            reverse ? 'md:-ml-xl' : 'md:-mr-xl',
            imageSize === 'small'
              ? 'aspect-image @max-[57.5rem]/imageblock:aspect-image md:aspect-square md:w-[420px]'
              : imageSize === 'medium'
                ? 'md:aspect-3/2 md:w-[330px] xl:w-[660px]'
                : 'md:aspect-3/2 md:w-[460px] xl:w-[910px]',
            'relative w-full transition-all duration-300 @min-[59.5rem]/imageblock:shrink-0',
            '@max-[57.5rem]/imageblock:mx-0 @max-[57.5rem]/imageblock:w-full'
          )}
        >
          <div
            className={cn(
              reverse ? 'bottom-0 md:right-0' : 'top-0 md:left-0',
              'md:w-s lg:w-m mix-blend-hard-light md:top-0 md:h-full',
              'h-s bg-blue-d absolute z-10 w-full',
              '@max-[57.5rem]/imageblock:h-s @max-[57.5rem]/imageblock:w-full',
              reverse
                ? '@max-[57.5rem]/imageblock:top-auto @max-[57.5rem]/imageblock:bottom-0'
                : '@max-[57.5rem]/imageblock:top-0 @max-[57.5rem]/imageblock:bottom-auto'
            )}
            style={{
              backgroundImage: `url(${previewUrl}${technicalTexture?.asset.sys.uri}?crop=800,800,center&q=75&f=webp)`,
              backgroundSize: 'cover',
              opacity: 1
            }}
          ></div>

          {video && image ? (
            <BlockVideo
              videoUrl={video.sys.uri}
              width={''}
              height={''}
              image={image}
              className=''
              btnPosition={'bottom-left'}
              btnClassName='size-[36px] !p-2xs'
            />
          ) : (
            image && (
              <Image
                image={image}
                classes='absolute -z-0 h-full w-full object-cover'
              />
            )
          )}
        </div>
        <div
          className={cn(
            // reverse ? 'pt-0' : 'pb-0',
            'space-y-m p-m @max-[57.5rem]/imageblock:sm:p-xl w-full md:p-0'
            // reverse
            //   ? '@max-[57.5rem]/imageblock:pt-0'
            //   : '@max-[57.5rem]/imageblock:pb-0'
          )}
        >
          <StandardText
            content={contentBlock}
            backgroundColour={backgroundColour}
            className='[&_p]:max-w-[80ch]'
          />
        </div>
      </div>
    </div>
  )
}

export default ImageTextOverlay
